import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Users,
  CreditCard,
  Activity,
  TrendingUp,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Eye,
  Plus,
  Minus,
  UserX,
  UserCheck,
  Crown,
  Loader2,
  Shield,
  BarChart3,
  Layers,
  Sun,
  Moon
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { user, token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [page, setPage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // User detail modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  
  // Action modals
  const [creditModalOpen, setCreditModalOpen] = useState(false);
  const [creditAction, setCreditAction] = useState({ amount: 0, reason: '' });
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [newPlan, setNewPlan] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const LIMIT = 20;

  // Check admin access
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/admin/check`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsAdmin(response.data.is_admin);
        if (!response.data.is_admin) {
          toast.error('Admin access required');
          navigate('/dashboard');
        }
      } catch (error) {
        toast.error('Access denied');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    
    if (token) {
      checkAdmin();
    }
  }, [token, navigate]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [token]);

  // Fetch users
  const fetchUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        skip: page * LIMIT,
        limit: LIMIT
      });
      if (searchTerm) params.append('search', searchTerm);
      if (planFilter !== 'all') params.append('plan', planFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await axios.get(`${API_URL}/api/admin/users?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
      setTotalUsers(response.data.total);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, [token, page, searchTerm, planFilter, statusFilter]);

  // Fetch user detail
  const fetchUserDetail = async (userId) => {
    setLoadingDetail(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserDetail(response.data);
    } catch (error) {
      toast.error('Failed to fetch user details');
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchStats();
      fetchUsers();
    }
  }, [isAdmin, fetchStats, fetchUsers]);

  // Handle user click
  const handleUserClick = (user) => {
    setSelectedUser(user);
    fetchUserDetail(user.id);
  };

  // Handle credit modification
  const handleCreditSubmit = async () => {
    if (!selectedUser || creditAction.amount === 0) return;
    
    setActionLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${selectedUser.id}/credits`,
        {
          user_id: selectedUser.id,
          amount: creditAction.amount,
          reason: creditAction.reason || 'Admin adjustment'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Credits ${creditAction.amount > 0 ? 'added' : 'removed'} successfully`);
      setCreditModalOpen(false);
      setCreditAction({ amount: 0, reason: '' });
      fetchUserDetail(selectedUser.id);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to modify credits');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle plan change
  const handlePlanSubmit = async () => {
    if (!selectedUser || !newPlan) return;
    
    setActionLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${selectedUser.id}/plan`,
        { user_id: selectedUser.id, new_plan: newPlan },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Plan updated successfully');
      setPlanModalOpen(false);
      setNewPlan('');
      fetchUserDetail(selectedUser.id);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update plan');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle deactivate/reactivate
  const handleToggleActive = async () => {
    if (!selectedUser) return;
    
    setActionLoading(true);
    const isActive = selectedUser.is_active !== false;
    const endpoint = isActive ? 'deactivate' : 'reactivate';
    
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${selectedUser.id}/${endpoint}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`User ${isActive ? 'deactivated' : 'reactivated'} successfully`);
      fetchUserDetail(selectedUser.id);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    } finally {
      setActionLoading(false);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get plan badge color
  const getPlanBadge = (plan) => {
    const colors = {
      free: 'bg-gray-500/20 text-gray-400',
      starter: 'bg-blue-500/20 text-blue-400',
      growth: 'bg-purple-500/20 text-purple-400',
      pro: 'bg-amber-500/20 text-amber-400'
    };
    return colors[plan] || colors.free;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-500/20 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <h1 className="text-xl font-semibold">Admin Dashboard</h1>
              <p className="text-sm text-muted-foreground">MockupPro Internal</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              <Layers className="w-4 h-4 mr-2" />
              Back to App
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <Users className="w-4 h-4" />
                  <span className="text-xs">Total Users</span>
                </div>
                <p className="text-2xl font-bold">{stats.total_users}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <Activity className="w-4 h-4" />
                  <span className="text-xs">Active (7d)</span>
                </div>
                <p className="text-2xl font-bold">{stats.active_users}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <Crown className="w-4 h-4" />
                  <span className="text-xs">Paid Users</span>
                </div>
                <p className="text-2xl font-bold">{stats.paid_users}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <CreditCard className="w-4 h-4" />
                  <span className="text-xs">Credits Today</span>
                </div>
                <p className="text-2xl font-bold">{stats.credits_used_today}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <BarChart3 className="w-4 h-4" />
                  <span className="text-xs">Images Today</span>
                </div>
                <p className="text-2xl font-bold">{stats.images_generated_today}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <TrendingUp className="w-4 h-4" />
                  <span className="text-xs">Total Credits</span>
                </div>
                <p className="text-2xl font-bold">{stats.total_credits_used}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Plan Breakdown */}
        {stats?.users_by_plan && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-base">Users by Plan</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 flex-wrap">
                {Object.entries(stats.users_by_plan).map(([plan, count]) => (
                  <div key={plan} className="flex items-center gap-2">
                    <Badge className={getPlanBadge(plan)}>{plan}</Badge>
                    <span className="text-sm font-medium">{count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Growth Metrics Section */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {/* Conversion Metrics */}
            {stats.conversion && (
              <Card className="border-blue-500/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-blue-500" />
                    Conversion Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">Free → Paid</span>
                    <span className="font-semibold text-blue-500">{stats.conversion.free_to_paid_rate}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">Trial → 1st Gen</span>
                    <span className="font-semibold">{stats.conversion.trial_to_first_gen_rate}%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">Gen → Upgrade</span>
                    <span className="font-semibold">{stats.conversion.gen_to_upgrade_rate}%</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Referral Metrics */}
            {stats.referrals && (
              <Card className="border-purple-500/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Users className="w-4 h-4 text-purple-500" />
                    Referral Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">Total Referrals</span>
                    <span className="font-semibold">{stats.referrals.total_referrals}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">Conversions</span>
                    <span className="font-semibold text-green-500">{stats.referrals.successful_conversions}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">Credits Awarded</span>
                    <span className="font-semibold text-purple-500">{stats.referrals.credits_awarded}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Revenue Metrics */}
            {stats.revenue && (
              <Card className="border-emerald-500/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-emerald-500" />
                    Revenue Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">MRR</span>
                    <span className="font-semibold text-emerald-500">${stats.revenue.mrr}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">Rev/User</span>
                    <span className="font-semibold">${stats.revenue.revenue_per_user}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">Avg Credits/User</span>
                    <span className="font-semibold">{stats.revenue.avg_credits_per_user}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Funnel Metrics */}
            {stats.funnel && (
              <Card className="border-amber-500/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <BarChart3 className="w-4 h-4 text-amber-500" />
                    This Month Funnel
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">Signups</span>
                    <span className="font-semibold">{stats.funnel.signups}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">1st Generations</span>
                    <span className="font-semibold">{stats.funnel.first_generations}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-muted-foreground">Upgrades</span>
                    <span className="font-semibold text-amber-500">{stats.funnel.upgrades}</span>
                  </div>
                  <div className="pt-2 border-t border-border">
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-muted-foreground">Signup→Gen</span>
                      <span className="font-medium">{stats.funnel.signup_to_gen_rate}%</span>
                    </div>
                    <div className="flex justify-between items-center text-xs mt-1">
                      <span className="text-muted-foreground">Gen→Upgrade</span>
                      <span className="font-medium">{stats.funnel.gen_to_upgrade_rate}%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* User List */}
        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row gap-4 justify-between">
              <CardTitle className="text-base">User Management</CardTitle>
              <div className="flex gap-2 flex-wrap">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search email..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                    className="pl-9 w-48"
                  />
                </div>
                <Select value={planFilter} onValueChange={(v) => { setPlanFilter(v); setPage(0); }}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Plans</SelectItem>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="starter">Starter</SelectItem>
                    <SelectItem value="growth">Growth</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="active">Subscribed</SelectItem>
                    <SelectItem value="inactive">No Sub</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={() => { fetchStats(); fetchUsers(); }}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-2 font-medium text-muted-foreground">Email</th>
                    <th className="text-left py-3 px-2 font-medium text-muted-foreground">Plan</th>
                    <th className="text-right py-3 px-2 font-medium text-muted-foreground">Credits</th>
                    <th className="text-right py-3 px-2 font-medium text-muted-foreground">Used</th>
                    <th className="text-left py-3 px-2 font-medium text-muted-foreground">Last Active</th>
                    <th className="text-left py-3 px-2 font-medium text-muted-foreground">Joined</th>
                    <th className="text-center py-3 px-2 font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-border/50 hover:bg-muted/30">
                      <td className="py-3 px-2">
                        <div>
                          <p className="font-medium">{u.email}</p>
                          <p className="text-xs text-muted-foreground">{u.id.slice(0, 8)}...</p>
                        </div>
                      </td>
                      <td className="py-3 px-2">
                        <Badge className={getPlanBadge(u.plan)}>{u.plan}</Badge>
                      </td>
                      <td className="py-3 px-2 text-right font-mono">{u.credits}</td>
                      <td className="py-3 px-2 text-right font-mono text-muted-foreground">{u.total_credits_used || 0}</td>
                      <td className="py-3 px-2 text-xs text-muted-foreground">
                        {u.last_activity ? formatDate(u.last_activity) : 'Never'}
                      </td>
                      <td className="py-3 px-2 text-xs text-muted-foreground">
                        {formatDate(u.created_at)}
                      </td>
                      <td className="py-3 px-2 text-center">
                        <Button variant="ghost" size="sm" onClick={() => handleUserClick(u)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Showing {page * LIMIT + 1}-{Math.min((page + 1) * LIMIT, totalUsers)} of {totalUsers}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={(page + 1) * LIMIT >= totalUsers}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* User Detail Dialog */}
      <Dialog open={!!selectedUser} onOpenChange={() => { setSelectedUser(null); setUserDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>User Details</DialogTitle>
            <DialogDescription>{selectedUser?.email}</DialogDescription>
          </DialogHeader>
          
          {loadingDetail ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          ) : userDetail ? (
            <div className="space-y-6">
              {/* User Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">User ID</Label>
                  <p className="font-mono text-sm">{userDetail.user.id}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Email</Label>
                  <p>{userDetail.user.email}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Plan</Label>
                  <Badge className={getPlanBadge(userDetail.user.plan)}>{userDetail.user.plan}</Badge>
                </div>
                <div>
                  <Label className="text-muted-foreground">Credits</Label>
                  <p className="font-mono text-lg">{userDetail.user.credits}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Total Credits Used</Label>
                  <p className="font-mono">{userDetail.total_credits_used}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Subscription ID</Label>
                  <p className="font-mono text-xs">{userDetail.user.subscription_id || 'None'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Stripe Customer</Label>
                  <p className="font-mono text-xs">{userDetail.user.stripe_customer_id || 'None'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Joined</Label>
                  <p className="text-sm">{formatDate(userDetail.user.created_at)}</p>
                </div>
              </div>

              {/* Usage Breakdown */}
              {Object.keys(userDetail.usage_breakdown).length > 0 && (
                <div>
                  <Label className="text-muted-foreground mb-2 block">Usage Breakdown</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {Object.entries(userDetail.usage_breakdown).map(([action, data]) => (
                      <div key={action} className="bg-muted/30 rounded p-2">
                        <p className="text-xs text-muted-foreground capitalize">{action.replace('_', ' ')}</p>
                        <p className="font-mono">{data.count} ({data.credits} cr)</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Credit History */}
              {userDetail.credit_history.length > 0 && (
                <div>
                  <Label className="text-muted-foreground mb-2 block">Recent Credit History</Label>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {userDetail.credit_history.slice(0, 10).map((tx, i) => (
                      <div key={i} className="flex justify-between text-sm bg-muted/30 rounded px-2 py-1">
                        <span className="text-muted-foreground">{tx.action}</span>
                        <span className={tx.amount > 0 ? 'text-green-500' : 'text-red-500'}>
                          {tx.amount > 0 ? '+' : ''}{tx.amount}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Admin Actions */}
              <div className="flex flex-wrap gap-2 pt-4 border-t border-border">
                <Button size="sm" onClick={() => setCreditModalOpen(true)}>
                  <Plus className="w-4 h-4 mr-1" /> Add Credits
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setCreditAction({ amount: 0, reason: '' }); setCreditModalOpen(true); }}>
                  <Minus className="w-4 h-4 mr-1" /> Remove Credits
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setNewPlan(userDetail.user.plan); setPlanModalOpen(true); }}>
                  <Crown className="w-4 h-4 mr-1" /> Change Plan
                </Button>
                <Button 
                  size="sm" 
                  variant={userDetail.user.is_active === false ? "default" : "destructive"}
                  onClick={handleToggleActive}
                  disabled={actionLoading}
                >
                  {userDetail.user.is_active === false ? (
                    <><UserCheck className="w-4 h-4 mr-1" /> Reactivate</>
                  ) : (
                    <><UserX className="w-4 h-4 mr-1" /> Deactivate</>
                  )}
                </Button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Credit Modal */}
      <Dialog open={creditModalOpen} onOpenChange={setCreditModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modify Credits</DialogTitle>
            <DialogDescription>Add or remove credits for {selectedUser?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Amount (positive to add, negative to remove)</Label>
              <Input
                type="number"
                value={creditAction.amount}
                onChange={(e) => setCreditAction({ ...creditAction, amount: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div>
              <Label>Reason</Label>
              <Textarea
                value={creditAction.reason}
                onChange={(e) => setCreditAction({ ...creditAction, reason: e.target.value })}
                placeholder="e.g., Customer support credit, refund, etc."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditModalOpen(false)}>Cancel</Button>
            <Button onClick={handleCreditSubmit} disabled={actionLoading || creditAction.amount === 0}>
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plan Modal */}
      <Dialog open={planModalOpen} onOpenChange={setPlanModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change Plan</DialogTitle>
            <DialogDescription>Update plan for {selectedUser?.email}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New Plan</Label>
              <Select value={newPlan} onValueChange={setNewPlan}>
                <SelectTrigger>
                  <SelectValue placeholder="Select plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="growth">Growth</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPlanModalOpen(false)}>Cancel</Button>
            <Button onClick={handlePlanSubmit} disabled={actionLoading || !newPlan}>
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Update Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
